'use client'

import type { ReactNode } from 'react'
import {
  ArrowRight,
  CalendarHeart,
  Camera,
  Droplets,
  Heart,
  MessageCircleHeart,
  Sparkles,
  type LucideIcon,
} from 'lucide-react'

import {
  BeautyFrame,
  FullBleedImage,
  SafeText,
  beautyInk,
  beautyIvory,
  beautyMuted,
  beautySans,
  beautySerif,
  clampIndex,
  getBeautyCopy,
  getBeautyDesign,
  getBeautyVisual,
  type BeautyPackProps,
} from '../shared/BeautyPackCore'

const bg = [
  'https://hubee.app/images/creator-studio/publications/carousel_templates/beauty_01/community_01.jpg',
  'https://hubee.app/images/creator-studio/publications/carousel_templates/beauty_01/community_02.jpg',
]

const total = 5

const fallbacks = [
  {
    eyebrow: 'Nuestra comunidad',
    headline: 'Nuestra piel, nuestra comunidad.',
    subheadline:
      'Rituales, tips y apoyo real para una piel saludable y una comunidad que inspira.',
    body: 'Una invitación para participar, compartir y sentirse acompañada en el proceso.',
    cta: 'Únete a la comunidad',
    result: 'Real skin. Real people. Real connection.',
  },
  {
    eyebrow: 'Ritual #1',
    headline: 'Comparte tu ritual glow.',
    subheadline:
      'Cuéntanos qué pasos no pueden faltar en tu día para sentirte bien en tu piel.',
    body:
      'Comparte tu rutina favorita y ayuda a otra persona a encontrar un ritual simple que pueda sostener.',
    cta: 'Siguiente',
  },
  {
    eyebrow: 'Ritual #2',
    headline: 'Tu tip puede inspirar a otras.',
    subheadline:
      'Ese consejo que cambió tu piel también puede cambiarle el día a alguien más.',
    body:
      'Aquí todas aprendemos juntas. Lo que para ti parece obvio, para otra persona puede ser el inicio de una nueva rutina.',
    cta: 'Siguiente',
  },
  {
    eyebrow: 'Ritual #3',
    headline: 'Constancia que se comparte.',
    subheadline:
      'Publica tu rutina semanal y mantengámonos motivadas a cuidar nuestra piel juntas.',
    body:
      'La comunidad convierte el cuidado personal en un hábito más ligero, real y fácil de repetir.',
    cta: 'Siguiente',
  },
  {
    eyebrow: 'Únete',
    headline: 'Más que piel, somos comunidad.',
    subheadline:
      'Gracias por ser parte de este espacio donde el cuidado se comparte y el amor propio crece.',
    body: 'Guarda este post, etiqueta a una amiga y empieza el ritual con nosotras.',
    cta: 'Únete a la comunidad',
    result: 'Real skin. Real people. Real connection.',
  },
]

type MiddleSlideConfig = {
  number: string
  count: string
  fallbackEyebrow: string
}

const middleSlideConfigs: Record<number, MiddleSlideConfig> = {
  1: {
    number: '02',
    count: '2/5',
    fallbackEyebrow: 'Ritual #1',
  },
  2: {
    number: '03',
    count: '3/5',
    fallbackEyebrow: 'Ritual #2',
  },
  3: {
    number: '04',
    count: '4/5',
    fallbackEyebrow: 'Ritual #3',
  },
}

const communityIconMap: Record<string, LucideIcon> = {
  droplets: Droplets,
  ritual: Droplets,
  routine: CalendarHeart,
  calendar: CalendarHeart,
  consistency: CalendarHeart,
  community: MessageCircleHeart,
  message: MessageCircleHeart,
  conversation: MessageCircleHeart,
  heart: Heart,
  love: Heart,
  share: Camera,
  camera: Camera,
  progress: Sparkles,
  glow: Sparkles,
  sparkle: Sparkles,
  care: Heart,
}

function getCommunityIconKey(copy: ReturnType<typeof getBeautyCopy>) {
  const rawSlide = copy as any
  const explicitKey = String(rawSlide.iconKey || rawSlide.icon || '').toLowerCase().trim()

  if (explicitKey && communityIconMap[explicitKey]) {
    return explicitKey
  }

  const text = `${copy.eyebrow || ''} ${copy.headline || ''} ${copy.subheadline || ''} ${copy.body || ''}`
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')

  if (text.includes('ritual') || text.includes('rutina')) return 'ritual'
  if (text.includes('constancia') || text.includes('semanal') || text.includes('habito')) return 'consistency'
  if (text.includes('comunidad') || text.includes('juntas') || text.includes('aprendemos')) return 'community'
  if (text.includes('comparte') || text.includes('publica') || text.includes('foto') || text.includes('video')) return 'share'
  if (text.includes('glow') || text.includes('progreso') || text.includes('inspir')) return 'glow'
  if (text.includes('amor') || text.includes('cuidado')) return 'heart'

  return 'sparkle'
}

function pickCommunityIcon(copy: ReturnType<typeof getBeautyCopy>): LucideIcon {
  const key = getCommunityIconKey(copy)
  return communityIconMap[key] || Sparkles
}

function splitHeadlineForAccent(text: string) {
  const clean = String(text || '').trim()
  const words = clean.split(/\s+/).filter(Boolean)

  if (words.length <= 2) {
    return {
      main: clean,
      accent: '',
    }
  }

  if (words.length <= 5) {
    return {
      main: words.slice(0, -1).join(' '),
      accent: words.slice(-1).join(' '),
    }
  }

  const accentCount = Math.min(3, Math.max(2, Math.floor(words.length * 0.3)))

  return {
    main: words.slice(0, -accentCount).join(' '),
    accent: words.slice(-accentCount).join(' '),
  }
}

function renderAccentHeadline(text: string, accent: string) {
  const { main, accent: accentText } = splitHeadlineForAccent(text)

  return (
    <>
      {main ? <span>{main}</span> : null}
      {main && accentText ? <br /> : null}
      {accentText ? (
        <span className="italic" style={{ color: accent }}>
          {accentText}
        </span>
      ) : null}
    </>
  )
}

function renderBodyWithEmphasis(text: string) {
  const clean = String(text || '').trim()
  if (!clean) return null

  const importantTerms = [
    'piel',
    'comunidad',
    'ritual',
    'rutina',
    'juntas',
    'compartir',
    'constancia',
    'aprendemos',
    'cuidado',
    'glow',
    'saludable',
    'inspirar',
    'motivar',
  ]

  const words = clean.split(/(\s+)/)

  return (
    <>
      {words.map((word, index) => {
        const normalized = word
          .toLowerCase()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .replace(/[.,;:!?¿¡“”"']/g, '')

        const shouldEmphasize = importantTerms.some((term) => {
          const normalizedTerm = term
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')

          return normalized === normalizedTerm
        })

        if (!shouldEmphasize) return <span key={index}>{word}</span>

        return (
          <strong key={index} className="font-bold">
            {word}
          </strong>
        )
      })}
    </>
  )
}

function SlideNumberBadge({
  value,
  accent,
}: {
  value: string
  accent: string
}) {
  return (
    <div
      className="absolute left-[28px] top-[28px] z-30 flex h-[46px] w-[46px] items-center justify-center rounded-full text-[14px] font-bold tracking-[0.03em] text-white"
      style={{
        background: accent,
        fontFamily: beautySans,
        boxShadow: '0 8px 24px rgba(0,0,0,0.10)',
      }}
    >
      {value}
    </div>
  )
}

function SlideCountBadge({
  value,
}: {
  value: string
}) {
  return (
    <div
      className="absolute right-[24px] top-[28px] z-30 rounded-full px-[13px] py-[8px] text-[13px] font-semibold text-white"
      style={{
        background: 'rgba(33,24,21,0.54)',
        fontFamily: beautySans,
      }}
    >
      {value}
    </div>
  )
}

function FineDivider({
  accent,
}: {
  accent: string
}) {
  return (
    <div
      className="mx-auto mt-[24px] h-px w-[148px]"
      style={{ background: `${accent}99` }}
    />
  )
}

function MiddleCommunitySlide({
  copy,
  accent,
  config,
}: {
  copy: ReturnType<typeof getBeautyCopy>
  accent: string
  config: MiddleSlideConfig
}) {
  const Icon = pickCommunityIcon(copy)

  return (
    <div className="absolute inset-0 overflow-hidden" style={{ background: beautyIvory }}>
      <div
        className="absolute inset-0 opacity-[0.38]"
        style={{
          background:
            'radial-gradient(circle at 50% 26%, rgba(177,138,74,0.12), transparent 34%), linear-gradient(180deg, rgba(255,255,255,0.74), rgba(246,238,227,0.58))',
        }}
      />

      <SlideNumberBadge value={config.number} accent={accent} />
      <SlideCountBadge value={config.count} />

      <div className="absolute left-[52px] right-[52px] top-[126px] z-20 flex flex-col items-center text-center">
        <Icon className="h-[54px] w-[54px]" strokeWidth={1.35} style={{ color: accent }} />

        <SafeText
          className="mt-[34px] text-[14px] font-bold uppercase tracking-[0.24em]"
          style={{ color: accent, fontFamily: beautySans }}
        >
          {copy.eyebrow || config.fallbackEyebrow}
        </SafeText>

        <SafeText
          className="mt-[26px] text-[58px] font-normal leading-[0.92] tracking-[-0.055em]"
          style={{ color: beautyInk, fontFamily: beautySerif }}
        >
          {renderAccentHeadline(copy.headline, accent)}
        </SafeText>

        <FineDivider accent={accent} />

        <SafeText
          className="mt-[28px] max-w-[340px] text-[19px] leading-[1.45]"
          style={{ color: 'rgba(33,24,21,0.82)', fontFamily: beautySans }}
        >
          {renderBodyWithEmphasis(copy.subheadline || copy.body)}
        </SafeText>
      </div>

      <ArrowRight
        className="absolute bottom-[54px] right-[42px] h-[36px] w-[36px]"
        strokeWidth={1.4}
        style={{ color: accent }}
      />
    </div>
  )
}

function CoverSlide({
  draft,
  copy,
  accent,
}: {
  draft: BeautyPackProps['draft']
  copy: ReturnType<typeof getBeautyCopy>
  accent: string
}) {
  return (
    <div className="absolute inset-0 overflow-hidden" style={{ background: beautyIvory }}>
      <FullBleedImage
        {...getBeautyVisual(draft, 0, bg[0], {
          overlay: 'white',
          opacity: 0.04,
        })}
        objectPosition="center bottom"
      />

      <div className="absolute inset-0 bg-gradient-to-b from-[#FFF9F0]/96 via-[#FFF9F0]/72 to-[#FFF9F0]/8" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/34 via-transparent to-transparent" />

      <div className="absolute left-[42px] right-[42px] top-[96px] z-20 text-center">
        <div
          className="mx-auto h-[260px] w-[260px] rounded-full border"
          style={{ borderColor: `${accent}66` }}
        />

        <SafeText
          className="absolute left-0 right-0 top-[54px] text-[62px] font-normal leading-[0.92] tracking-[-0.055em]"
          style={{ color: beautyInk, fontFamily: beautySerif }}
        >
          {renderAccentHeadline(copy.headline, accent)}
        </SafeText>

        <SafeText
          className="mx-auto mt-[24px] max-w-[300px] text-[14px] font-bold uppercase leading-[1.45] tracking-[0.17em]"
          style={{ color: 'rgba(33,24,21,0.72)', fontFamily: beautySans }}
        >
          {copy.subheadline || copy.body}
        </SafeText>
      </div>

      <div
        className="absolute bottom-0 left-0 right-0 z-20 px-6 py-5 text-center text-[11px] font-bold uppercase tracking-[0.18em] text-white"
        style={{
          background: 'rgba(96,94,52,0.92)',
          fontFamily: beautySans,
        }}
      >
        {copy.result || 'Real skin. Real people. Real connection.'}
      </div>
    </div>
  )
}

function ClosingSlide({
  draft,
  copy,
  accent,
}: {
  draft: BeautyPackProps['draft']
  copy: ReturnType<typeof getBeautyCopy>
  accent: string
}) {
  return (
    <div className="absolute inset-0 overflow-hidden" style={{ background: beautyIvory }}>
      <FullBleedImage
        {...getBeautyVisual(draft, 4, bg[1], {
          overlay: 'white',
          opacity: 0.05,
        })}
        objectPosition="center bottom"
      />

      <div className="absolute inset-0 bg-gradient-to-b from-[#FFF9F0]/96 via-[#FFF9F0]/78 to-[#FFF9F0]/8" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/28 via-transparent to-transparent" />

      <div className="absolute left-[42px] right-[42px] top-[92px] z-20 text-center">
        <SafeText
          className="mx-auto max-w-[340px] text-[58px] font-normal leading-[0.92] tracking-[-0.055em]"
          style={{ color: beautyInk, fontFamily: beautySerif }}
        >
          {renderAccentHeadline(copy.headline || copy.cta, accent)}
        </SafeText>

        <FineDivider accent={accent} />

        <SafeText
          className="mx-auto mt-[26px] max-w-[310px] text-[18px] leading-[1.45]"
          style={{ color: 'rgba(33,24,21,0.80)', fontFamily: beautySans }}
        >
          {copy.subheadline || copy.result || copy.body}
        </SafeText>

        <div
          className="mx-auto mt-[28px] inline-flex items-center justify-center gap-2 rounded-[14px] border px-[22px] py-[12px] text-[12px] font-bold uppercase tracking-[0.14em]"
          style={{
            borderColor: `${accent}66`,
            color: accent,
            background: 'rgba(255,255,255,0.56)',
            fontFamily: beautySans,
          }}
        >
          <Heart className="h-4 w-4" strokeWidth={1.7} />
          {copy.cta || 'Únete a la comunidad'}
        </div>
      </div>

      <div
        className="absolute bottom-0 left-0 right-0 z-20 flex items-center justify-between px-6 py-5 text-[11px] font-bold uppercase tracking-[0.16em] text-white"
        style={{
          background: 'rgba(96,94,52,0.92)',
          fontFamily: beautySans,
        }}
      >
        <div className="flex items-center gap-2">
          <MessageCircleHeart className="h-5 w-5" strokeWidth={1.6} />
          <span>{copy.cta || 'Comenta para unirte'}</span>
        </div>

        <span>Guardar</span>
      </div>
    </div>
  )
}

export default function BeautyCommunityPack({
  draft,
  activeIndex = 0,
  onActiveIndexChange,
  showControls = true,
  exportMode = false,
}: BeautyPackProps) {
  const index = clampIndex(activeIndex, total)
  const design = getBeautyDesign(draft, '#A77B35')
  const accent = design.accent || '#A77B35'

  const copy = getBeautyCopy(draft, index, fallbacks[index])

  return (
    <BeautyFrame
      activeIndex={index}
      total={total}
      onActiveIndexChange={onActiveIndexChange}
      showControls={showControls}
      nav="dark"
      className="bg-[#FFF9F0]"
      borderRadius={exportMode ? 0 : 34}
    >
      {index === 0 ? (
        <CoverSlide draft={draft} copy={copy} accent={accent} />
      ) : null}

      {index === 1 ? (
        <MiddleCommunitySlide
          copy={copy}
          accent={accent}
          config={middleSlideConfigs[1]}
        />
      ) : null}

      {index === 2 ? (
        <MiddleCommunitySlide
          copy={copy}
          accent={accent}
          config={middleSlideConfigs[2]}
        />
      ) : null}

      {index === 3 ? (
        <MiddleCommunitySlide
          copy={copy}
          accent={accent}
          config={middleSlideConfigs[3]}
        />
      ) : null}

      {index === 4 ? (
        <ClosingSlide draft={draft} copy={copy} accent={accent} />
      ) : null}
    </BeautyFrame>
  )
}